import { useEffect, useState } from 'react';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { buzz, playTick } from './chime';

export const FOCUS_TIMER_ID = 'focus-timer';
/** Keep in sync with the manifest accent. */
export const FOCUS_ACCENT = '#e5484d';

/** Re-render every `intervalMs` while `enabled`; returns the current time. */
export function useNow(enabled: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    // Align ticks to the wall-clock second so the display never skips a digit.
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      setNow(Date.now());
      interval = setInterval(() => setNow(Date.now()), intervalMs);
    }, intervalMs - (Date.now() % intervalMs));
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [enabled, intervalMs]);
  return now;
}

export interface FocusSettings {
  focusMinutes: number;
  breakMinutes: number;
  sound: boolean;
}

export function useFocusSettings(): FocusSettings {
  // Subscribe to rows so saved settings show up without a reload.
  useExtensionsStore((s) => s.rows);
  const values = useExtensionsStore.getState().getSettings(FOCUS_TIMER_ID);
  return {
    focusMinutes: Number(values.focusMinutes ?? 25),
    breakMinutes: Number(values.breakMinutes ?? 5),
    sound: values.sound !== 'off',
  };
}

/**
 * Feedback for a press or a dial detent: a quiet click when the timer's sound
 * is on, and a short buzz on phones that can. Every fifth minute is a firmer one.
 */
export function useClick(): (kind?: 'press' | 'minor' | 'major') => void {
  const { sound } = useFocusSettings();
  return (kind = 'press') => {
    if (sound) playTick(kind);
    buzz(kind === 'major' ? 10 : 5);
  };
}
